//-- Copyright (c) 2021 TMT Insights Group, LLC
import { HTMLAttributes, ReactNode } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Fade, Typography } from '@mui/material';
import { convertStylesToStyled } from '../../_utils/utils';

type SpinnerProps = HTMLAttributes<HTMLDivElement> & {
  size?: number;
  className?: string;
  label?: string | ReactNode;
  contained?: boolean;
};

const Spinner = ({ size = 100, className = '', label, contained = false, ...props }: SpinnerProps) => {
  if (label) {
    return (
      <Fade in timeout={600}>
        <FullPage>
          <SpinnerWrapper data-target="spinner-wrapper" size={size} contained={contained} className={className} {...props} />
          <Typography variant="h2" sx={{ ml: 3 }}>
            {label}
          </Typography>
        </FullPage>
      </Fade>
    );
  }

  return <SpinnerWrapper data-target="spinner-wrapper" size={size} contained={contained} className={className} {...props} />;
};

export default Spinner;

const Spinny = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const SpinnerWrapper = styled.div<{ size: number; contained: SpinnerProps['contained'] }>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  position: ${({ contained }) => (contained ? 'relative' : 'absolute')};
  top: 50%;
  left: 50%;
  margin: ${({ size }) => `-${size / 2}px 0 0 -${size / 2}px`};
  user-select: none;
  border-radius: 50%;
  box-shadow: ${({ theme }) =>
    `${theme.palette.primary.dark} 0px 2px 2px, ${theme.palette.primary.main} 0.5px 0.866px 2px, ${theme.palette.primary.light} 1.866px 1px 2px, ${theme.palette.secondary.dark} 2px 0px 2px, ${theme.palette.secondary.main} 1px -1px 2px, ${theme.palette.secondary.light} 0px -2px 2px`};
  filter: saturate(400%) contrast(0.9);
  animation: ${Spinny} 1s linear infinite;
  z-index: 999;

  ${({ theme }) => convertStylesToStyled(theme.customOverrides?.Spinner)}
`;

const FullPage = styled.div`
  position: fixed;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  padding-top: 70px;
  background: ${({ theme }) => theme.palette.background.default + 'bf'};
  z-index: 999;

  & div[data-target='spinner-wrapper'] {
    position: relative;
    left: auto;
    top: auto;
    margin: 0;
    height: 60px;
    width: 60px;
  }
`;
